import type { ReactNode } from 'react'
import type { OrderStage } from '../lib/orders-list'
import { tr, type Lang, type Pair } from '../lib/i18n'
import O from '../locales/orders.json'
import C from '../locales/collect.json'
import F from '../locales/final-check.json'
import S from '../locales/send-out.json'
import K from '../locales/cash-in.json'

// R572 (b1): one order on the till's Orders face. The card names its stage, says when the order
// rings now, and carries the stage's card body (the existing screen's own row) and its ONE action.

/** R572 (b2): the two cards from (b0)'s cash-due read, for "Delivered, cash in". */
export type CashDueStage = 'outForDelivery' | 'deliveredNoRecord'

export const STAGE_LABEL: Record<OrderStage | CashDueStage, Pair> = {
  inFlight: O.stageInProgress,
  finalCheck: F.finalCheckTitle,
  pickup: C.readyForPickup,
  sendOut: S.readyToSend,
  outForDelivery: K.stageOutForDelivery,
  deliveredNoRecord: K.stageDeliveredNoRecord,
}

interface OrderCardProps {
  id: string
  stage: OrderStage | CashDueStage
  ringing: boolean
  lang: Lang
  children?: ReactNode
  /** The stage's one action (none while an order in flight waits for the kitchen). */
  action?: ReactNode
}

export function OrderCard({ id, stage, ringing, lang, children, action }: OrderCardProps) {
  return (
    <li
      data-order-id={id}
      data-stage={stage}
      data-ringing={ringing ? 'true' : 'false'}
      className={
        ringing
          ? 'rounded-xl border-2 border-ember-500 bg-white px-4 py-3 space-y-2'
          : 'rounded-xl border border-fleet-300 bg-white px-4 py-3 space-y-2'
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <span data-stage-label className="rounded-full bg-navy-tint text-navy text-xs font-semibold px-2 py-0.5">
          {tr(STAGE_LABEL[stage], lang)}
        </span>
        {ringing && (
          <span data-rings-now className="rounded-full bg-ember-100 text-ember-700 text-xs font-semibold px-2 py-0.5">
            {tr(O.ringsNow, lang)}
          </span>
        )}
      </div>
      {children}
      {action ? <div data-card-action>{action}</div> : null}
    </li>
  )
}
